// src/Redux/slices/postsSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  uploadFiles,
  fetchPosts,
  fetchPostsByUser,
  createPost,
  updatePost,
  deletePost,
} from "../thunks/postsThunks";

const initialState = {
  list: [],
  total: 0,
  status: "idle",
  error: null,

  // ✅ État pour l'upload
  uploadStatus: "idle",
  uploadError: null,
  uploadedFiles: [],
  uploadSuccessMessage: null,
  successMessage: null,


  // ✅ Bucket pour le feed global (tous les posts)
  feed: {
    items: [],
    total: 0,
    status: "idle",
    error: null,
  },

  byUser: {},
};

const postsSlice = createSlice({
  name: "posts",
  initialState,
  reducers: {
    clearPostsError(state) {
      state.error = null;
      state.uploadError = null;
      state.feed.error = null;
    },

    clearUploadState(state) {
      state.uploadStatus = "idle";
      state.uploadError = null;
      state.uploadedFiles = [];
      state.uploadSuccessMessage = null;
    },

    // ✅ NEW: clear only upload success message
    clearUploadSuccessMessage(state) {
      state.uploadSuccessMessage = null;
    },

    // ✅ NEW: clear only generic success message (create/update/delete...)
    clearSuccessMessage(state) {
      state.successMessage = null;
    },

    resetPostsState() {
      return {
        ...initialState,
        byUser: {},
      };
    },
  },

  extraReducers: (builder) => {
    /* ================== UPLOAD FILES ================== */
    builder
      .addCase(uploadFiles.pending, (state) => {
        state.uploadStatus = "uploading";
        state.uploadError = null;
        state.uploadedFiles = [];
        state.uploadSuccessMessage = null; // ✅ important
      })
      .addCase(uploadFiles.fulfilled, (state, action) => {
        state.uploadStatus = "succeeded";
        state.uploadedFiles = action.payload || [];
        state.uploadSuccessMessage = "✅ Files uploaded successfully!";
      })
      .addCase(uploadFiles.rejected, (state, action) => {
        state.uploadStatus = "failed";
        state.uploadError = action.payload || "Failed to upload files";
        state.uploadedFiles = [];
        state.uploadSuccessMessage = null; // ✅ important
      });

    /* ================== FETCH ALL POSTS (FEED) ================== */
    builder
      .addCase(fetchPosts.pending, (state) => {
        state.feed.status = "loading";
        state.feed.error = null;
        state.status = "loading";
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        // ✅ Le backend retourne { data: [...], total: 22, page: 1 }
        const { data, total, page } = action.payload || {};
        
        state.feed.status = "succeeded";
        state.feed.items = page === 1 ? (data || []) : [...state.feed.items, ...(data || [])];
        state.feed.total = total || 0;
        state.feed.error = null;
        
        state.status = "succeeded";
        
        // ✅ Aussi mettre à jour list pour compatibilité
        state.list = state.feed.items;
        state.total = state.feed.total;
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.feed.status = "failed";
        state.feed.error = action.payload || "Failed to fetch posts";
        state.status = "failed";
        state.error = action.payload || "Failed to fetch posts";
      });

    /* ================== FETCH POSTS BY USER ================== */
    builder
      .addCase(fetchPostsByUser.pending, (state, action) => {
        const { arg } = action.meta || {};
        const userId = arg?.userId;
        if (!userId) return;

        if (!state.byUser[userId]) {
          state.byUser[userId] = {
            list: [],
            total: 0,
            status: "idle",
            error: null,
          };
        }
        state.byUser[userId].status = "loading";
        state.byUser[userId].error = null;
      })
      .addCase(fetchPostsByUser.fulfilled, (state, action) => {
        const { userId, items, total } = action.payload || {};
        if (!userId) return;

        state.byUser[userId] = {
          list: items || [],
          total: total || 0,
          status: "succeeded",
          error: null,
        };
      })
      .addCase(fetchPostsByUser.rejected, (state, action) => {
        const { arg } = action.meta || {};
        const userId = arg?.userId;
        if (!userId) return;

        if (!state.byUser[userId]) {
          state.byUser[userId] = {
            list: [],
            total: 0,
            status: "failed",
            error: action.payload || "Failed to fetch posts by user",
          };
        } else {
          state.byUser[userId].status = "failed";
          state.byUser[userId].error =
            action.payload || "Failed to fetch posts by user";
        }
      });

    /* ================== CREATE POST ================== */
    builder
      .addCase(createPost.pending, (state) => {
        state.error = null;
        state.successMessage = null; // ✅ important
      })
      .addCase(createPost.fulfilled, (state, action) => {
        const post = action.payload;
        if (!post) return;

        // ✅ Feed global
        state.feed.items.unshift(post);
        state.feed.total += 1;

        // Feed legacy
        state.list.unshift(post);
        state.total += 1;

        state.successMessage = "✅ Post published successfully!";

        const userId = post.userId;
        if (userId) {
          const bucket =
            state.byUser[userId] || {
              list: [],
              total: 0,
              status: "idle",
              error: null,
            };

          bucket.list.unshift(post);
          bucket.total += 1;
          if (!bucket.status || bucket.status === "idle") {
            bucket.status = "succeeded";
          }

          state.byUser[userId] = bucket;
        }

        // ✅ Clear upload state after successful creation
        state.uploadStatus = "idle";
        state.uploadedFiles = [];
        state.uploadSuccessMessage = null;
      })
      .addCase(createPost.rejected, (state, action) => {
        state.error = action.payload || "Failed to create post";
      });

    /* ================== UPDATE POST ================== */
    builder
      .addCase(updatePost.pending, (state) => {
        state.error = null;
      })
      .addCase(updatePost.fulfilled, (state, action) => {
        const updated = action.payload;
        if (!updated || !updated.id) return;

        // ✅ Feed global
        const idxFeed = state.feed.items.findIndex(
          (p) => (p.id || p._id) === (updated.id || updated._id)
        );
        if (idxFeed !== -1) {
          state.feed.items[idxFeed] = {
            ...state.feed.items[idxFeed],
            ...updated,
          };
        }

        // Feed legacy
        const idx = state.list.findIndex(
          (p) => (p.id || p._id) === (updated.id || updated._id)
        );
        if (idx !== -1) {
          state.list[idx] = { ...state.list[idx], ...updated };
        }

        const userId = updated.userId;
        if (userId && state.byUser[userId]) {
          const arr = state.byUser[userId].list;
          const idxUser = arr.findIndex(
            (p) => (p.id || p._id) === (updated.id || updated._id)
          );
          if (idxUser !== -1) {
            arr[idxUser] = { ...arr[idxUser], ...updated };
          }
        }
      })
      .addCase(updatePost.rejected, (state, action) => {
        state.error = action.payload || "Failed to update post";
      });

    /* ================== DELETE POST ================== */
    builder
      .addCase(deletePost.pending, (state) => {
        state.error = null;
      })
      .addCase(deletePost.fulfilled, (state, action) => {
        const { postId } = action.payload || {};
        if (!postId) return;

        const idStr = String(postId);

        // ✅ Feed global
        state.feed.items = state.feed.items.filter(
          (p) => (p.id || p._id)?.toString() !== idStr
        );
        if (state.feed.total > 0) state.feed.total -= 1;

        // Feed legacy
        state.list = state.list.filter(
          (p) => (p.id || p._id)?.toString() !== idStr
        );
        if (state.total > 0) state.total -= 1;

        Object.keys(state.byUser).forEach((uid) => {
          const bucket = state.byUser[uid];
          if (!bucket) return;

          const beforeLen = bucket.list.length;
          bucket.list = bucket.list.filter(
            (p) => (p.id || p._id)?.toString() !== idStr
          );
          if (bucket.total && beforeLen !== bucket.list.length) {
            bucket.total = Math.max(0, bucket.total - 1);
          }
        });
      })
      .addCase(deletePost.rejected, (state, action) => {
        state.error = action.payload || "Failed to delete post";
      });
  },
});

export const {
  clearPostsError,
  clearUploadState,
  clearUploadSuccessMessage,
  clearSuccessMessage,
  resetPostsState,
} = postsSlice.actions;

/* ============== SELECTORS ============== */
export const selectPostsList = (state) => state.posts?.list || [];
export const selectPostsTotal = (state) => state.posts?.total || 0;
export const selectPostsStatus = (state) => state.posts?.status || "idle";
export const selectPostsError = (state) => state.posts?.error || null;

// ✅ Selectors pour le feed
export const selectFeedPosts = (state) => state.posts?.feed?.items || [];
export const selectFeedTotal = (state) => state.posts?.feed?.total || 0;
export const selectFeedStatus = (state) => state.posts?.feed?.status || "idle";
export const selectFeedError = (state) => state.posts?.feed?.error || null;

// ✅ Selectors pour l'upload
// Upload selectors
export const selectUploadStatus = (state) => state.posts?.uploadStatus || "idle";
export const selectUploadError = (state) => state.posts?.uploadError || null;
export const selectUploadedFiles = (state) => state.posts?.uploadedFiles || [];
export const selectUploadSuccessMessage = (state) =>
  state.posts?.uploadSuccessMessage || null;

// Success selectors
export const selectSuccessMessage = (state) => state.posts?.successMessage || null;

export const selectPostsByUser = (state, userId) =>
  state.posts?.byUser?.[userId]?.list || [];

export const selectPostsByUserStatus = (state, userId) =>
  state.posts?.byUser?.[userId]?.status || "idle";

export const selectPostsByUserTotal = (state, userId) =>
  state.posts?.byUser?.[userId]?.total || 0;

export default postsSlice.reducer;
